import React, { memo, useCallback, useState } from "react";
import styles from "./styles.module.scss";
import DropdownButton from "app/components/DropdownButton";
import AddToRecruitProfile from "app/components/Button/AddToRecruitProfile";
import movePostIcon from "app/images/move-post-icon.svg";
import copyPostIcon from "app/images/copy-paste-icon.svg";
import editIcon from "app/images/edit-icon.svg";
import tagsIcon from "app/images/tags-icon.svg";
import pinIcon from "app/images/pin-icon-vector.svg";
import unpinIcon from "app/images/unpin-icon-vector.svg";
import reportIcon from "app/images/Alert.svg";
import deleteIcon from "app/images/trash-icon.svg";
import PostModel from "app/models/PostModel";
import {
  canAddToRecruit,
  canCopy,
  canDelete,
  canEdit,
  canMove,
  planCanPin,
} from "../../Post";
import { cls } from "app/utils";
import { shallowEqual, useSelector } from "react-redux";
import { RootState } from "typedefs";
import { useTranslation } from "react-i18next";
import PinnedPostsPaywall from "app/components/paywalls/PinnedPosts";
import { useLocation } from "react-router-dom";

interface MenuItem {
  label?: string;
  icon?: string;
  className?: string;
  permission?: boolean;
  onClick?: (post: PostModel) => void;
  children?: React.ReactNode;
  hide?: boolean;
  icon_classname?: string;
}

interface Props {
  post: PostModel;
  hideButtonStyles?: boolean;
  buttonClassName?: string;
  isReply?: boolean;
  onMove?: () => void;
  onCopy?: () => void;
  onEdit?: () => void;
  onEditTags?: () => void;
  onPin?: () => void;
  onDelete?: (post: PostModel) => void;
  onReport?: (post: PostModel) => void;
  dropdownClassName?: string;
  canPinPost?: boolean;
}

const PostMenu = ({ post, ...rest }: Props) => {
  const { canPinPost = true } = rest;
  const { t } = useTranslation();
  const { self } = useSelector((state: RootState) => state.user, shallowEqual);
  const [showModal, setPaywallModal] = useState(false);
  const location = useLocation();

  const isMainFeed = /^\/$/.test(window.location.pathname);

  const onPaywallOpen = useCallback(() => {
    setPaywallModal(true);
  }, []);

  const onPaywallClose = useCallback(() => {
    setPaywallModal(false);
  }, []);

  if (!self) {
    return null;
  }

  const menuList: MenuItem[] = [
    {
      children: <AddToRecruitProfile />,
      permission: canAddToRecruit(post) && !rest.isReply,
      hide: location.pathname.includes("schedule"),
    },
    {
      label: t("Move to") + " ...",
      icon: movePostIcon,
      permission: canMove(post) && !rest.isReply,
      onClick: () => {
        rest.onMove();
      },
      hide: location.pathname.includes("schedule"),
    },
    {
      label: t("Copy to") + " ...",
      icon: copyPostIcon,
      permission: canCopy(post) && !rest.isReply,
      onClick: () => {
        rest.onCopy();
      },
      hide: location.pathname.includes("schedule"),
    },
    {
      label: t("Edit"),
      icon: editIcon,
      permission: canEdit(post),
      onClick: () => {
        rest.onEdit();
      },
    },
    {
      label: t("Edit Tags"),
      icon: tagsIcon,
      permission: canEdit(post) && !rest.isReply,
      onClick: () => {
        rest.onEditTags();
      },
      hide: rest.isReply,
    },
    {
      label: t("Pin to top"),
      icon: pinIcon,
      permission: !rest?.isReply && !isMainFeed && !post?.scheduledAt,
      onClick: () => {
        if (planCanPin(post) && canPinPost) {
          rest.onPin();
        } else {
          onPaywallOpen();
        }
      },
      hide: !canPinPost || (post && post.pinnedByName),
    },
    {
      label: t("Unpin"),
      icon: unpinIcon,
      permission: !rest?.isReply && !isMainFeed && !post?.scheduledAt,
      icon_classname: styles.unpin_icon,
      onClick: () => {
        if (planCanPin(post) && canPinPost) {
          rest.onPin();
        } else {
          onPaywallOpen();
        }
      },
      hide: !canPinPost || !(post && post.pinnedByName),
    },
    {
      label: t("Report"),
      icon: reportIcon,
      permission: self.id !== post.userId,
      onClick: (postData: PostModel) => {
        rest.onReport(postData);
      },
    },
    {
      label: t("Delete"),
      icon: deleteIcon,
      className: styles.delete_btn,
      permission: canDelete(post),
      onClick: (postData) => {
        rest.onDelete(postData);
      },
    },
  ];

  return (
    <DropdownButton
      btnContent={<i className="bullets" />}
      btnClassName={rest.buttonClassName}
      hideButtonStyles={rest.hideButtonStyles}
      dropdownWrapperClass={cls(
        styles.dropdown_container,
        rest.dropdownClassName,
      )}
      closeOnClick
    >
      <div className={styles.container}>
        {menuList.map((list, index) => {
          if (list.permission && !list.hide) {
            return list.children ? (
              <React.Fragment key={index}>{list.children}</React.Fragment>
            ) : (
              list.onClick && list.permission && (
                <button key={list.label} onClick={() => list.onClick(post)}>
                  <img
                    src={list.icon}
                    alt={list.label}
                    className={cls(styles.menu_icon, list.icon_classname)}
                  />
                  <p className={cls(list.className)}>{list.label}</p>
                </button>
              )
            );
          } else {
            return null;
          }
        })}
      </div>
      <PinnedPostsPaywall show={showModal} onClose={onPaywallClose} />
    </DropdownButton>
  );
};

export default memo(PostMenu);
